import { z } from 'zod';
import { checkContainerNumber } from '../iso6346/iso6346';

/** Container-specific job data captured at intake (PRD → Job intake → Captured per job type (Container); gitaumoses4/chukua#31). */

const codeSchema = z.string().trim().min(1).max(32);

/** `POST /jobs/:id/containers`, `PATCH /jobs/:id/containers/:containerId` — one container on the job. */
export const containerInputSchema = z.object({
  number: z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      const result = checkContainerNumber(value);
      if (!result.ok) ctx.addIssue({ code: 'custom', message: result.reason, params: { problem: result.problem } });
    }),
  sizeCode: codeSchema,
  typeCode: codeSchema,
  seal: z.string().trim().min(1).max(32).optional(),
  /** The declared gross weight (VGM) in kg. Omitted on an edit keeps the stored value; null clears it. */
  grossWeightKg: z.number().int().positive().nullable().optional(),
});
export type ContainerInput = z.infer<typeof containerInputSchema>;

/** Where release paperwork stands — a deployment's own words for the wait aren't in the PRD, so this is a closed set of three. */
export const RELEASE_STATUSES = ['pending', 'received', 'verified'] as const;
export type ReleaseStatus = (typeof RELEASE_STATUSES)[number];

/**
 * `PUT /jobs/:id/container-details` — one row per job. The empty-return depot and its
 * free-days deadline are mandatory on every container job (the ticket's own wording);
 * everything else here can be filled in as it's learned, same as the job's own quick-create.
 */
export const containerDetailInputSchema = z.object({
  shippingLine: z.string().trim().min(1).max(120).optional(),
  vessel: z.string().trim().min(1).max(120).optional(),
  billOfLading: z.string().trim().min(1).max(64).optional(),
  releaseOrder: z.string().trim().min(1).max(64).optional(),
  releaseStatus: z.enum(RELEASE_STATUSES).optional(),
  pickupZoneCode: codeSchema.optional(),
  emptyReturnDepotZoneCode: codeSchema,
  emptyReturnFreeDaysDeadline: z.iso.date(),
  isTransit: z.boolean().default(false),
  isHazardous: z.boolean().default(false),
  bondReference: z.string().trim().min(1).max(64).optional(),
});
export type ContainerDetailInput = z.infer<typeof containerDetailInputSchema>;
